import { getDateFormatKey, momentDatePattern } from 'src/util/date/dateFormatOptions';

let currentLocale = 'de-DE';

export function setDateLocale(locale: string): void {
	currentLocale = locale;
}

export function formatLongDate(date: Date, timeZone?: string): string {
	return new Intl.DateTimeFormat(currentLocale, {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		...(timeZone !== undefined ? { timeZone } : {}),
	}).format(date);
}

export function formatDisplayDate(date: Date): string {
	const pattern = momentDatePattern(getDateFormatKey());
	return pattern === null ? formatLongDate(date) : formatDate(date, pattern);
}

export function formatDisplayDateTime(date: Date): string {
	return `${formatDisplayDate(date)} ${formatDate(date, 'HH:mm')}`;
}

export function formatWeekdayDisplayDate(date: Date): string {
	return `${formatDate(date, 'ddd')}, ${formatDisplayDate(date)}`;
}

function dayNameLong(d: Date): string {
	return new Intl.DateTimeFormat(currentLocale, { weekday: 'long' }).format(d);
}

function dayNameShort(d: Date): string {
	return new Intl.DateTimeFormat(currentLocale, { weekday: 'short' }).format(d);
}

function monthNameLong(d: Date): string {
	return new Intl.DateTimeFormat(currentLocale, { month: 'long' }).format(d);
}

function monthNameShort(d: Date): string {
	return new Intl.DateTimeFormat(currentLocale, { month: 'short' }).format(d);
}

function pad(n: number, width = 2): string {
	return String(n).padStart(width, '0');
}

export function formatDate(value: Date | number | string, format: string): string {
	const d = value instanceof Date ? value : new Date(value);

	if (isNaN(d.getTime())) {
		return '';
	}

	const hours24 = d.getHours();
	const hours12 = hours24 % 12 || 12;

	const tokenReplacements: Record<string, string> = {
		D: String(d.getDate()),
		DD: pad(d.getDate()),
		H: String(hours24),
		HH: pad(hours24),
		M: String(d.getMonth() + 1),
		MM: pad(d.getMonth() + 1),
		MMM: monthNameShort(d),
		MMMM: monthNameLong(d),
		YY: String(d.getFullYear()).slice(-2),
		YYYY: String(d.getFullYear()),
		ddd: dayNameShort(d),
		dddd: dayNameLong(d),
		h: String(hours12),
		hh: pad(hours12),
		m: String(d.getMinutes()),
		mm: pad(d.getMinutes()),
		s: String(d.getSeconds()),
		ss: pad(d.getSeconds()),
	};

	return format.replace(
		/dddd|ddd|YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s/g,
		(token) => tokenReplacements[token]
	);
}

export function addToDate(
	value: Date | number | string,
	mod: {
		days?: number;
		hours?: number;
		milliseconds?: number;
		minutes?: number;
		months?: number;
		seconds?: number;
		years?: number;
	}
): Date {
	const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);

	if (mod.years) d.setFullYear(d.getFullYear() + mod.years);
	if (mod.months) d.setMonth(d.getMonth() + mod.months);
	if (mod.days) d.setDate(d.getDate() + mod.days);
	if (mod.hours) d.setHours(d.getHours() + mod.hours);
	if (mod.minutes) d.setMinutes(d.getMinutes() + mod.minutes);
	if (mod.seconds) d.setSeconds(d.getSeconds() + mod.seconds);
	if (mod.milliseconds) d.setMilliseconds(d.getMilliseconds() + mod.milliseconds);

	return d;
}

export function today(): string {
	const d = new Date();
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function getDateDiff(date1: Date | number | string, date2: Date | number | string, unit: string): number {
	const d1 = date1 instanceof Date ? date1 : new Date(date1);
	const d2 = date2 instanceof Date ? date2 : new Date(date2);
	const diffMs = d1.getTime() - d2.getTime();

	switch (unit) {
		case 'seconds':
			return Math.floor(diffMs / 1000);
		case 'minutes':
			return Math.floor(diffMs / (1000 * 60));
		case 'hours':
			return Math.floor(diffMs / (1000 * 60 * 60));
		case 'days':
			return Math.floor(diffMs / (1000 * 60 * 60 * 24));
		default:
			return diffMs;
	}
}

export function parseWallClockAsLocal(wallClockAsUtc: string): Date {
	return new Date(wallClockAsUtc.replace(/Z$/, ''));
}
